import React, { useState } from 'react';
import axios from 'axios';

import { useStyles } from '~/assets/styles';
import { useClaim } from '~/components/ClaimContainer';
import IncidentTypeEditConfirmDialog from '~/components/Fnol/NewFnolUI/IncidentDetails/IncidentTypeEditConfirmDialog';
import { useCms } from '~/components/hooks/useCms';
import { useLob } from '~/components/hooks/useLob';
import IncidentTypeUpdateDialog from '~/components/IncidentTypeUpdateDialog';
import { ShowOnlyTextField } from '~/components/TextFieldFormik';
import { incidentHasTypeDesc } from '~/components/types/claim-types';
import { isConfigurableFnol, reportAxiosError } from '~/Utils';
import type { PopulatedClaim } from '~/Utils/populateClaim';

import GenericClaimBarField from './GenericClaimBarField';

type IncidentTypeProps = {
  displayName?: string;
};

type IncidentTypeUpdateValues = {
  incident_type: string;
  incident_sub_type: string;
};

type IncidentTypeWithEditProps = {
  claim: PopulatedClaim;
  incident: NonNullable<PopulatedClaim['incident']>;
  incidentTypesDict: NonNullable<PopulatedClaim['incident_types_dict']>;
  lob: string;
  onAsyncClaimUpdate: () => void;
};

const IncidentTypeWithEdit = ({
  claim,
  incident,
  incidentTypesDict,
  lob,
  onAsyncClaimUpdate,
}: IncidentTypeWithEditProps) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showIncidentTypeUpdateDialog, setShowIncidentTypeUpdateDialog] = useState(false);

  const onUpdate = async (values: IncidentTypeUpdateValues) => {
    const url = claim.type === 'general_claim' ? `/api/v1/claims/${claim.id}` : `/api/v1/${lob}s/${claim.id}`;
    try {
      await axios.patch(url, { incident: values });
      onAsyncClaimUpdate();
    } catch (error) {
      reportAxiosError(error);
    }
  };

  const onEditRequested = () => {
    setShowConfirmModal(true);
  };

  const onClose = () => {
    setShowConfirmModal(false);
    setShowIncidentTypeUpdateDialog(false);
  };

  const onConfirm = () => {
    setShowConfirmModal(false);
    setShowIncidentTypeUpdateDialog(true);
  };

  const { classes } = useStyles();

  return (
    <>
      <ShowOnlyTextField
        showOnlyValueComponent={claim.incident_type_desc}
        label=""
        onEdit={onEditRequested}
        classes={classes}
        doNotRenderInTypography
        wrapperClassName="m-0"
        editableValueStyles={{}}
      />

      <IncidentTypeEditConfirmDialog isOpen={showConfirmModal} onCancel={onClose} onConfirm={onConfirm} />

      {showIncidentTypeUpdateDialog && (
        <IncidentTypeUpdateDialog
          incidentTypesDict={incidentTypesDict}
          incidentType={incident.incident_type || ''}
          incidentSubType={incident.incident_sub_type || ''}
          onUpdate={onUpdate}
          onClose={onClose}
        />
      )}
    </>
  );
};

const IncidentType = ({ displayName = 'Incident Type' }: IncidentTypeProps) => {
  const { userOrganization } = useCms();
  const { claim, onAsyncClaimUpdate } = useClaim({ claimRequired: true });
  const { lob, claimType } = useLob();

  const incidentTypeDescription: string | null =
    claim.incident_type_desc || (incidentHasTypeDesc(claim.incident) && claim.incident.incident_type_desc) || null;

  const content =
    isConfigurableFnol({ userOrganization, claimType, claim }) && claim.incident && claim.incident_types_dict ? (
      <IncidentTypeWithEdit
        claim={claim}
        incident={claim.incident}
        incidentTypesDict={claim.incident_types_dict}
        lob={lob}
        onAsyncClaimUpdate={onAsyncClaimUpdate}
      />
    ) : (
      incidentTypeDescription
    );

  const title = lob === 'home_claim' ? 'Peril' : displayName;

  return content ? <GenericClaimBarField title={title} content={content} allowWrapping={false} /> : null;
};

export default IncidentType;
